/**
 * useCommentMediaWatchers
 *
 * Live single-comment listeners for video replies — Flutter
 * `_subscribeToContentReply` (comments_bottom_sheet.dart:859-889), "used to
 * refresh video processing state". Waits for the transcode Cloud Function to
 * merge the real `mediaInfo` onto the comment doc
 * (cloud_functions/functions/src/functions/content/content_utils.ts:188-212)
 * and re-renders the row with the finished thumbnail, no refresh needed.
 *
 * Split out of `useContentComments` so the subscription registry owns its own
 * lifecycle (conventions.md § "New responsibility -> new owner file").
 */

import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { Unsubscribe } from 'firebase/firestore';
import { subscribeToCommentUseCase } from '@/domain/usecases/comment/subscribeToCommentUseCase';
import { mergeServerComment } from './mergeServerComment';
import type { CommentEntity } from '@/domain/entities/comment/commentEntity';

export interface UseCommentMediaWatchersResult {
  /**
   * Open a live listener on one comment so its placeholder `mediaInfo` is
   * swapped for the real one once the transcoder stamps it onto the doc.
   * Flutter `_subscribeToContentReply` (comments_bottom_sheet.dart:859-889);
   * the only caller invokes it once, right after `_addComment` resolves (:927).
   */
  watchCommentMedia: (commentId: string) => void;
}

export function useCommentMediaWatchers(
  setComments: Dispatch<SetStateAction<CommentEntity[]>>,
): UseCommentMediaWatchersResult {
  // Flutter `_contentReplyStreamSubscriptions` (comments_bottom_sheet.dart:888).
  // Keyed by comment id (Flutter uses a bare list) so a repeat watch request for
  // the same comment cannot open a second Firestore listener.
  const watchersRef = useRef<Map<string, Unsubscribe>>(new Map());
  const mountedRef = useRef(true);

  // ORDERING MATTERS: this effect is declared FIRST so its cleanup runs before
  // the teardown below, guaranteeing `watchCommentMedia` sees `false` and can
  // never register a listener that the teardown has already passed.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Flutter cancels every collected subscription in dispose()
  // (comments_bottom_sheet.dart:154-156); closing the sheet must leave no live
  // listener behind.
  useEffect(() => {
    const watchers = watchersRef.current;
    return () => {
      watchers.forEach(unsubscribe => unsubscribe());
      watchers.clear();
    };
  }, []);

  const watchCommentMedia = useCallback((commentId: string) => {
    // The comments sheet may already be gone: `useCommentVideoReply` fires the
    // post-publish write unconditionally (useCommentVideoReply.ts:146-161) and
    // only then asks for the listener. Flutter cannot reach this state (its
    // State stays alive under the pushed add-content route), and a listener
    // opened after the unmount cleanup above would never be unsubscribed —
    // the write survives an unmounted host, the listener deliberately does not.
    if (!mountedRef.current) return;

    // Idempotent — a re-entrant call must not double-subscribe.
    if (watchersRef.current.has(commentId)) return;

    // Flutter applies `DocumentChangeType.modified` changes ONLY (:871), so the
    // first snapshot's `added` replay of the already-written doc is ignored.
    // The web data service emits the whole doc on subscribe, i.e. that same
    // replay — treating it as a delta is the first-snapshot-baseline trap
    // (workflow/lessons.md), so the first emission only sets the baseline.
    let isFirstSnapshot = true;

    const unsubscribe = subscribeToCommentUseCase.execute(commentId, comment => {
      if (isFirstSnapshot) {
        isFirstSnapshot = false;
        return;
      }
      // Doc-gone (query emptied). Flutter cannot express this case — a removal
      // is not a `modified` change — so there is nothing to apply.
      if (!comment) return;
      if (!mountedRef.current) return;

      // Flutter keeps one flat `_comments` list and replaces the entry at the
      // matched index (:875-879); web nests replies under their parent, so the
      // update is applied across both levels.
      setComments(prev => prev.map(c => {
        if (c.id === commentId) return mergeServerComment(c, comment);
        if (c.children?.some(ch => ch.id === commentId)) {
          return {
            ...c,
            children: c.children.map(ch =>
              ch.id === commentId ? mergeServerComment(ch, comment) : ch,
            ),
          };
        }
        return c;
      }));
    });

    watchersRef.current.set(commentId, unsubscribe);
  }, [setComments]);

  return { watchCommentMedia };
}
